import React from "react";
import { Box, Typography, CircularProgress, Alert } from "@mui/material";
import {
  People as PeopleIcon,
  Quiz as QuizIcon,
  TrendingUp as TrendingUpIcon,
  Games as GamesIcon,
} from "@mui/icons-material";
import { useWidgetData } from "../../hooks/useWidgets";
import WidgetHeader from "./WidgetHeader";

const MetricWidget = ({ config, onEdit, onDelete }) => {
  const { data, isLoading, error } = useWidgetData(
    config.dataSource,
    config.timeRange
  );

  const getIcon = (iconName) => {
    switch (iconName) {
      case "people":
        return <PeopleIcon />;
      case "quiz":
        return <QuizIcon />;
      case "trending_up":
        return <TrendingUpIcon />;
      case "games":
        return <GamesIcon />;
      default:
        return null;
    }
  };

  const formatValue = (value, format) => {
    if (value === undefined || value === null) return "0";

    switch (format) {
      case "percentage":
        return `${value}%`;
      case "number":
      default:
        return value.toLocaleString();
    }
  };

  const getMetricValue = () => {
    if (!data) return null;

    // For different data sources, extract the appropriate metric
    switch (config.dataSource) {
      case "userSignups":
        return config.timeRange === "all" ? data.overall : data.total;
      case "quizGames":
        if (config.title.toLowerCase().includes("completion")) {
          return data.completionRate;
        }
        return data.total;
      case "questionsCreated":
        if (config.title.toLowerCase().includes("active")) {
          return data.active;
        }
        return data.total;
      case "leaderboard":
        return data.topPlayers?.length || 0;
      default:
        return data.total || data.overall || 0;
    }
  };

  const metricValue = getMetricValue();

  return (
    <Box
      sx={{
        p: 3,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
        borderRadius: 4,
        border: 1,
        borderColor: "divider",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
      }}
    >
      <WidgetHeader
        title={config.title}
        onEdit={onEdit ? () => onEdit(config) : null}
        onDelete={onDelete ? () => onDelete(config) : null}
        showDragHandle={Boolean(onEdit || onDelete)}
      />

      {/* Widget Content */}
      <Box
        flex={1}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        {isLoading ? (
          <CircularProgress />
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <>
            {/* Icon */}
            {config.settings?.icon && (
              <Box
                sx={{
                  color: config.settings?.color || "#2196F3",
                  mb: 2,
                }}
              >
                {getIcon(config.settings.icon)}
              </Box>
            )}

            {/* Metric Value */}
            <Typography
              variant="h3"
              component="div"
              sx={{
                color: config.settings?.color || "#2196F3",
                fontWeight: "bold",
                mb: 1,
              }}
            >
              {formatValue(metricValue, config.settings?.format)}
            </Typography>

            {/* Additional Info */}
            {config.dataSource === "quizGames" && data?.completionRate && (
              <Typography variant="body2" color="text.secondary">
                {data.completed} completed of {data.total} total
              </Typography>
            )}

            {config.dataSource === "questionsCreated" && data?.activeRate && (
              <Typography variant="body2" color="text.secondary">
                {data.active} active of {data.total} total
              </Typography>
            )}

            {/* Time Range Indicator */}
            <Typography variant="caption" color="text.secondary" mt={1}>
              {config.timeRange === "all"
                ? "All time"
                : `Last ${config.timeRange}`}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

export default MetricWidget;
